import React, { useState, useEffect } from 'react';
import './AccessibilityMenu.css';

function AccessibilityMenu({ isOpen, onClose }) {
  const [colorMode, setColorMode] = useState(localStorage.getItem('colorMode') || 'normal');
  const [textSize, setTextSize] = useState(parseInt(localStorage.getItem('textSize')) || 100);

  // Applica le impostazioni quando cambiano
  useEffect(() => {
    const root = document.documentElement;

    // Rimuovi tutti i filtri precedenti
    root.classList.remove(
      'color-protanopia',
      'color-deuteranopia',
      'color-tritanopia',
      'color-high-contrast',
      'color-monochrome'
    );

    // Applica il filtro selezionato
    if (colorMode !== 'normal') {
      root.classList.add(`color-${colorMode}`);
    }

    // Applica dimensione testo
    root.style.setProperty('--text-scale', textSize / 100);

    // Salva in localStorage
    localStorage.setItem('colorMode', colorMode);
    localStorage.setItem('textSize', textSize);
  }, [colorMode, textSize]);

  // Carica impostazioni salvate all'avvio
  useEffect(() => {
    const savedColorMode = localStorage.getItem('colorMode');
    const savedTextSize = localStorage.getItem('textSize');

    if (savedColorMode) setColorMode(savedColorMode);
    if (savedTextSize) setTextSize(parseInt(savedTextSize));
  }, []);

  // Chiusura con Escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Blocca scroll del body
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  const handleReset = () => {
    setColorMode('normal');
    setTextSize(100);
    localStorage.removeItem('colorMode');
    localStorage.removeItem('textSize');
  };

  const colorModes = [
    {
      id: 'normal',
      icon: '👁️',
      label: 'Visione Normale',
      sublabel: 'Colori standard'
    },
    {
      id: 'protanopia',
      icon: '🔴',
      label: 'Protanopia',
      sublabel: 'Daltonismo rosso-verde'
    },
    {
      id: 'deuteranopia',
      icon: '🟢',
      label: 'Deuteranopia',
      sublabel: 'Daltonismo verde-rosso'
    },
    {
      id: 'tritanopia',
      icon: '🔵',
      label: 'Tritanopia',
      sublabel: 'Daltonismo blu-giallo'
    },
    {
      id: 'high-contrast',
      icon: '⚫',
      label: 'Alto Contrasto',
      sublabel: 'Contrasto massimo'
    },
    {
      id: 'monochrome',
      icon: '⚪',
      label: 'Monocromatico',
      sublabel: 'Scala di grigi'
    }
  ];

  if (!isOpen) return null;

  return (
    <div
      className="accessibility-overlay"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="accessibility-modal-title"
    >
      <div
        className="accessibility-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="accessibility-modal-header">
          <div className="accessibility-modal-title">
            <span className="accessibility-modal-icon" aria-hidden="true" style={{ fontWeight: 'bold' }}>A</span>
            <h3 id="accessibility-modal-title">Impostazioni Accessibilità</h3>
          </div>
          <button
            className="accessibility-close-btn"
            onClick={onClose}
            aria-label="Chiudi impostazioni accessibilità"
          >
            <span aria-hidden="true">✕</span>
          </button>
        </div>

        {/* Body */}
        <div className="accessibility-modal-body">

          {/* Sezione Modalità Colore */}
          <div className="accessibility-section">
            <h4 className="accessibility-section-title">Modalità Colore</h4>
            <p className="accessibility-section-description">
              Seleziona una modalità adatta alle tue esigenze visive.
              Le impostazioni vengono salvate automaticamente.
            </p>
            <div className="accessibility-options">
              {colorModes.map((mode) => (
                <button
                  key={mode.id}
                  className={`accessibility-option ${colorMode === mode.id ? 'active' : ''}`}
                  onClick={() => setColorMode(mode.id)}
                  aria-label={`${mode.label}: ${mode.sublabel}`}
                  aria-pressed={colorMode === mode.id}
                >
                  <span className="accessibility-option-icon" aria-hidden="true">
                    {mode.icon}
                  </span>
                  <span className="accessibility-option-label">{mode.label}</span>
                  <span className="accessibility-option-sublabel">{mode.sublabel}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Sezione Dimensione Testo */}
          <div className="accessibility-section">
            <h4 className="accessibility-section-title">Dimensione Testo</h4>
            <p className="accessibility-section-description">
              Aumenta o diminuisci la dimensione del testo per una migliore leggibilità.
            </p>
            <div className="text-size-control">
              <div className="text-size-slider-container">
                <label htmlFor="text-size-slider" className="text-size-label">
                  Scala testo
                </label>
                <input
                  id="text-size-slider"
                  type="range"
                  min="80"
                  max="150"
                  step="10"
                  value={textSize}
                  onChange={(e) => setTextSize(parseInt(e.target.value))}
                  className="text-size-slider"
                  aria-valuemin="80"
                  aria-valuemax="150"
                  aria-valuenow={textSize}
                  aria-label="Controllo dimensione testo"
                />
                <span className="text-size-value" aria-live="polite">
                  {textSize}%
                </span>
              </div>
            </div>
          </div>

          {/* Pulsante Reset */}
          <button
            className="accessibility-reset-btn"
            onClick={handleReset}
            aria-label="Ripristina tutte le impostazioni di accessibilità ai valori predefiniti"
          >
            Ripristina Impostazioni Predefinite
          </button>

        </div>
      </div>
    </div>
  );
}

export default AccessibilityMenu;
